import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon, CheckIcon, PencilIcon, TrashIcon, EllipsisVerticalIcon, ChatBubbleLeftRightIcon, EyeSlashIcon, UserPlusIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Task, WaitingStatus } from '../../types/task.types';
import { Avatar } from '../Avatar';
import { StatusBadge } from '../StatusBadge';
import { UrgencyBadge, UrgencyLevel } from '../UrgencyBadge';
import { isOverdue } from '@/core/utils/date.utils';
import './TaskItem.styles.css';

interface TaskItemProps {
  task: Task;
  onMarkDone: (taskId: string) => void;
  onUnmarkDone?: (taskId: string) => void;
  onMarkUnread?: (taskId: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onOpenChat: (task: Task) => void;
  onAssign?: (task: Task) => void;
  onViewEmail?: (emailId: string) => void;
  onStatusChange?: (taskId: string, newStatus: WaitingStatus) => void;
  onUrgencyChange?: (taskId: string, newUrgency: UrgencyLevel) => void;
  isLoading?: boolean;
  canEdit: boolean;
  canDelete: boolean;
  density?: 'comfortable' | 'compact';
  userRole?: string;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onMarkDone,
  onUnmarkDone,
  onMarkUnread,
  onEdit,
  onDelete,
  onOpenChat,
  onAssign,
  onViewEmail,
  onStatusChange,
  onUrgencyChange,
  isLoading = false,
  canEdit,
  canDelete,
  density = 'comfortable',
  userRole = 'admin'
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Get the workflow status badge type (only user-selectable statuses)
  const getStatusBadgeType = () => {
    if (task.is_done) return 'termine';
    // Check waiting_status for all badge types
    if (task.waiting_status) {
      const validStatuses = ['a_faire', 'acheve', 'pour_info', 'en_attente_infos',
                            'a_transmettre', 'en_attente_informations', 'message_laisse'] as const;
      if (validStatuses.includes(task.waiting_status as typeof validStatuses[number])) {
        return task.waiting_status;
      }
    }
    // Default to "en_cours" - workflow status is separate from priority/deadline
    return 'en_cours';
  };

  // Check if task has urgent or overdue indicator (shown separately from workflow status)
  const isUrgent = task.criticity === 'urgent' && !task.is_done;
  const isTaskOverdue = !!(task.deadline && isOverdue(task.deadline) && !task.is_done);

  // Handler for status change from StatusBadge dropdown
  const handleStatusChange = (newStatus: WaitingStatus) => {
    // All statuses go through onStatusChange (termine is no longer in dropdown)
    if (onStatusChange) {
      onStatusChange(task.id, newStatus);
    }
  };

  // Handler for urgency change from UrgencyBadge dropdown
  const handleUrgencyChange = (newUrgency: UrgencyLevel) => {
    if (onUrgencyChange) {
      onUrgencyChange(task.id, newUrgency);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-BE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTaskClick = () => {
    // Only open chat for non-informative tasks
    if (typeof onOpenChat === 'function' && task.task_type !== 'informative') {
      onOpenChat(task);
    }
  };

  return (
    <article 
      className={`mes-taches-task-card ${task.is_done ? 'task-done' : ''} ${((parseInt(String(task.unread_count ?? 0), 10) > 0) || task.is_new) ? 'has-unread' : ''} ${density === 'compact' ? 'compact' : ''} ${showMenu ? 'dropdown-open' : ''}`}
      onClick={density === 'compact' ? handleTaskClick : undefined}
      style={density === 'compact' ? { cursor: 'pointer' } : undefined}
    >
      {/* Checkbox - Far Left */}
      <div className="mes-taches-checkbox-wrapper">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (task.is_done && onUnmarkDone) {
              onUnmarkDone(task.id);
            } else {
              onMarkDone(task.id);
            }
          }}
          className={`mes-taches-checkbox ${task.is_done ? 'checked' : ''}`}
          disabled={isLoading}
          aria-label={task.is_done ? "Marquer comme non terminé" : "Marquer comme terminé"}
        >
          {task.is_done && <CheckIcon className="mes-taches-checkbox-icon" />}
        </button>
      </div>

      {/* Main Content - Clickable in normal mode only */}
      <div className="mes-taches-task-content" onClick={density !== 'compact' ? handleTaskClick : undefined}>
        {/* Left Zone: Task Details */}
        <div className="mes-taches-task-left-zone">
          <div className="mes-taches-task-title-wrapper">
            <h3 className="mes-taches-task-title">{task.message}</h3>
            {/* Chat messages indicator */}
            <div className="mes-taches-messages-wrapper">
              {/* Total messages count */}
              {parseInt(String(task.comment_count ?? 0), 10) > 0 && (
                <div className="mes-taches-total-messages">
                  <ChatBubbleLeftRightIcon className="mes-taches-chat-icon" />
                  <span className="mes-taches-chat-count">{parseInt(String(task.comment_count ?? 0), 10)}</span>
                </div>
              )}
              {/* New message notification */}
              {parseInt(String(task.unread_count ?? 0), 10) > 0 && (
                <div className="mes-taches-new-message-indicator">
                  <span className="mes-taches-notification-badge">{parseInt(String(task.unread_count ?? 0), 10)}</span>
                  <span className="mes-taches-notification-text">nouveau{parseInt(String(task.unread_count ?? 0), 10) > 1 ? 'x messages' : ' message'}</span>
                </div>
              )}
            </div>
          </div>
        {/* Only show metadata in left zone if NOT compact */}
        {density !== 'compact' && (
          <>
            {/* Metadata Row */}
            <div className="mes-taches-task-metadata">
              {/* Due Date */}
              {task.deadline && (
                <span className="mes-taches-meta-item">
                  <CalendarIcon className="mes-taches-meta-icon" />
                  {formatDate(task.deadline)}
                </span>
              )}

              {/* Email Source Badge */}
              {task.source_email_id && onViewEmail && (
                <button
                  className="mes-taches-email-badge"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewEmail(task.source_email_id!);
                  }}
                  title={task.source_email_subject || "Voir l'email original"}
                >
                  <EnvelopeIcon className="mes-taches-email-icon" />
                  <span className="mes-taches-email-subject">{task.source_email_subject || 'Email'}</span>
                </button>
              )}

              {/* Voicemail Source Badge */}
              {task.source_voicemail_id && (
                <span
                  className="mes-taches-meta-item"
                  title="Tâche créée depuis un message vocal (page Appels)"
                >
                  📞 Message vocal
                </span>
              )}
            </div>

            {/* Creator info - only in normal mode */}
            <div className="mes-taches-task-creator-line">
              <span>Par {task.created_by_name} • {formatDate(task.created_at)}</span>
            </div>
          </>
        )}
      </div>
      
      {/* Right Zone: Status + Assignees + Actions */}
      <div className="mes-taches-task-right-zone">
        {/* Show status and subtitle in right zone if compact */}
        {density === 'compact' && (
          <>
            {/* Status Badge + Urgency Badge and Menu Button together */}
            <div className="mes-taches-status-action-row">
              <div className="mes-taches-badges-row">
                <StatusBadge
                  type={getStatusBadgeType()}
                  interactive={!task.is_done && canEdit}
                  onStatusChange={handleStatusChange}
                  disabled={isLoading}
                />
                <UrgencyBadge
                  level={(task.criticity || 'medium') as UrgencyLevel}
                  isOverdue={isTaskOverdue}
                  interactive={!task.is_done && canEdit}
                  onUrgencyChange={handleUrgencyChange}
                  disabled={isLoading}
                />
              </div>

              {/* Assign Button for unassigned tasks */}
              {onAssign && task.needs_assignment && (userRole === 'admin' || userRole === 'bureau') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssign(task);
                  }}
                  className="mes-taches-assign-btn"
                  aria-label="Assigner la tâche"
                  title="Assigner la tâche"
                >
                  <UserPlusIcon className="mes-taches-assign-icon" />
                  <span>Assigner</span>
                </button>
              )}

              {/* Actions Menu Button - next to status */}
              <div className="mes-taches-task-actions" ref={menuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="mes-taches-menu-btn"
                  aria-label="Plus d'actions"
                >
                  <EllipsisVerticalIcon className="mes-taches-menu-icon" />
                </button>
                
                {/* Dropdown Menu */}
                {showMenu && (
                  <div className="mes-taches-dropdown-menu">
                    {/* Edit */}
                    {canEdit && !task.is_done && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(task);
                          setShowMenu(false);
                        }}
                        className="mes-taches-menu-item"
                      >
                        <PencilIcon className="mes-taches-menu-item-icon" />
                        <span>Modifier</span>
                      </button>
                    )}

                    {/* Assign - Only for admin/bureau users on unassigned tasks */}
                    {onAssign && task.needs_assignment && (userRole === 'admin' || userRole === 'bureau') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAssign(task);
                          setShowMenu(false);
                        }}
                        className="mes-taches-menu-item"
                      >
                        <UserPlusIcon className="mes-taches-menu-item-icon" />
                        <span>Assigner</span>
                      </button>
                    )}

                    {/* Mark as unread */}
                    {onMarkUnread && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkUnread(task.id);
                          setShowMenu(false);
                        }}
                        className="mes-taches-menu-item"
                      >
                        <EyeSlashIcon className="mes-taches-menu-item-icon" />
                        <span>Marquer comme non lu</span>
                      </button>
                    )}
                    
                    {/* Delete */}
                    {canDelete && (
                      <>
                        <div className="mes-taches-menu-divider"></div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Voulez-vous vraiment supprimer cette tâche?')) {
                              onDelete(task.id);
                            }
                            setShowMenu(false);
                          }}
                          className="mes-taches-menu-item mes-taches-menu-item-danger"
                        >
                          <TrashIcon className="mes-taches-menu-item-icon" />
                          <span>Supprimer</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        
        {/* Assignees - only show if not compact */}
        {density !== 'compact' && task.assigned_user_names && task.assigned_user_names.length > 0 && (
          <div className="mes-taches-task-assignees">
            {task.assigned_user_names.slice(0, 3).map((name, index) => (
              <Avatar key={index} name={name} size="small" />
            ))}
            {task.assigned_user_names.length > 3 && (
              <span className="mes-taches-assignees-more">+{task.assigned_user_names.length - 3}</span>
            )}
          </div>
        )}
        
        {/* Status Badge + Urgency Badge - only in normal mode */}
        {density !== 'compact' && (
          <div className="mes-taches-badges-row">
            <StatusBadge
              type={getStatusBadgeType()}
              interactive={!task.is_done && canEdit}
              onStatusChange={handleStatusChange}
              disabled={isLoading}
            />
            <UrgencyBadge
              level={(task.criticity || 'medium') as UrgencyLevel}
              isOverdue={isTaskOverdue}
              interactive={!task.is_done && canEdit}
              onUrgencyChange={handleUrgencyChange}
              disabled={isLoading}
            />
          </div>
        )}
        
        {/* Actions Menu Button - only in normal mode */}
        {density !== 'compact' && (
        <div className="mes-taches-task-actions" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="mes-taches-menu-btn"
            aria-label="Plus d'actions"
          >
            <EllipsisVerticalIcon className="mes-taches-menu-icon" />
          </button>
          
          {/* Dropdown Menu */}
          {showMenu && (
            <div className="mes-taches-dropdown-menu">
              {/* Edit */}
              {canEdit && !task.is_done && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                    setShowMenu(false);
                  }}
                  className="mes-taches-menu-item"
                >
                  <PencilIcon className="mes-taches-menu-item-icon" />
                  <span>Modifier</span>
                </button>
              )}

              {/* Assign - Only for admin/bureau users on unassigned tasks */}
              {onAssign && task.needs_assignment && (userRole === 'admin' || userRole === 'bureau') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssign(task);
                    setShowMenu(false);
                  }}
                  className="mes-taches-menu-item"
                >
                  <UserPlusIcon className="mes-taches-menu-item-icon" />
                  <span>Assigner</span>
                </button>
              )}

              {/* Mark as unread */}
              {onMarkUnread && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkUnread(task.id);
                    setShowMenu(false);
                  }}
                  className="mes-taches-menu-item"
                >
                  <EyeSlashIcon className="mes-taches-menu-item-icon" />
                  <span>Marquer comme non lu</span>
                </button>
              )}
              
              {/* Delete */}
              {canDelete && (
                <>
                  <div className="mes-taches-menu-divider"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Voulez-vous vraiment supprimer cette tâche?')) {
                        onDelete(task.id);
                      }
                      setShowMenu(false);
                    }}
                    className="mes-taches-menu-item mes-taches-menu-item-danger"
                  >
                    <TrashIcon className="mes-taches-menu-item-icon" />
                    <span>Supprimer</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      </div>
      
      {/* Bottom line for compact mode - all info */}
      {density === 'compact' && (
        <div
          className="mes-taches-task-bottom-line"
          onClick={handleTaskClick}
          style={{ cursor: 'pointer' }}
        >
          {/* Left side: sender, receivers, other info */}
          <div className="mes-taches-bottom-left">
            {/* Sender card */}
            <div className="mes-taches-sender-card">
              <span className="mes-taches-sender-label">De:</span>
              <span className="mes-taches-sender-name">{task.created_by_name}</span>
              <span className="mes-taches-sender-date">{formatDate(task.created_at)} à {formatTime(task.created_at)}</span>
            </div>

            {/* Receivers card */}
            {((task.assigned_user_names && task.assigned_user_names.length > 0) ||
              (task.assigned_roles && task.assigned_roles.length > 0)) && (
              <div className="mes-taches-receivers-card">
                <span className="mes-taches-receivers-label">À:</span>
                <span className="mes-taches-receivers-names">
                  {task.assigned_user_names && task.assigned_user_names.join(', ')}
                  {task.assigned_user_names && task.assigned_user_names.length > 0 &&
                   task.assigned_roles && task.assigned_roles.length > 0 &&
                   task.assigned_roles.filter(role => role && role !== '-').length > 0 && ', '}
                  {task.assigned_roles && task.assigned_roles.length > 0 &&
                    task.assigned_roles.filter(role => role && role !== '-').join(', ')}
                </span>
              </div>
            )}

            {/* Due Date */}
            {task.deadline && (
              <>
                <span className="mes-taches-separator">│</span>
                <span className="mes-taches-meta-item">
                  <CalendarIcon className="mes-taches-meta-icon" />
                  {formatDate(task.deadline)}
                </span>
              </>
            )}

            {/* Email Source Badge in compact mode */}
            {task.source_email_id && onViewEmail && (
              <>
                <span className="mes-taches-separator">│</span>
                <button
                  className="mes-taches-email-badge"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewEmail(task.source_email_id!);
                  }}
                  title={task.source_email_subject || "Voir l'email original"}
                >
                  <EnvelopeIcon className="mes-taches-email-icon" />
                  <span className="mes-taches-email-subject">{task.source_email_subject || 'Email'}</span>
                </button>
              </>
            )}

            {/* Voicemail Source Badge in compact mode */}
            {task.source_voicemail_id && (
              <>
                <span className="mes-taches-separator">│</span>
                <span
                  className="mes-taches-meta-item"
                  title="Tâche créée depuis un message vocal (page Appels)"
                >
                  📞 Message vocal
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
};